import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import { formatDate } from '../../util/datetime';
import EditIcon from '@mui/icons-material/Edit';

import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
  userHoldsExactRole,
} from '../../auth/AuthContext';
import * as tenantsApi from '../../api/tenants';
import * as devicesApi from '../../api/devices';
import { listTenantEvents } from '../../api/crashReports';
import {
  clearProcedureHiddenMetrics,
  getMetricCatalogue,
  getProcedureHiddenMetrics,
  setProcedureHiddenMetrics,
  type MetricCatalogueItem,
} from '../../api/sessionMetrics';
import { listProcedures, type Procedure } from '../../api/procedures';
import { prettyMetric } from '../../util/metricLabels';
import { extractErrorMessage } from '../../api/errors';
import { TenantFormDialog } from '../admin/TenantFormDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CohortBaseline } from './CohortBaseline';
import { TenantAdmins } from './TenantAdmins';
import { TenantMessages } from './TenantMessages';
import { AuditTrail } from '../admin/AuditTrail';
import { SsoConnectionPanel } from '../admin/SsoConnectionPanel';
import { SearchableSelect } from '../../components/SearchableSelect';
import { IntakeSettings } from '../../components/intake/IntakeSettings';
import { RecordingSettings } from '../../components/settings/RecordingSettings';
import { TenantLicensePanel } from '../../components/license/TenantLicensePanel';

const TZ_DEFAULT_OPTION = [{ value: '', label: 'Each viewer’s local time zone (default)' }];
const TZ_OPTIONS = [{ value: 'UTC', label: 'UTC' }];
const ALL_PROCEDURES_OPTION = [{ value: '', label: 'All procedures (tenant default)' }];

/**
 * Tab order → URL key. The last three (cohort / admins / messages) fold the
 * former standalone pages in as tabs; their old routes redirect to
 * `/tenant/settings?tab=<key>`.
 */
/**
 * Settings is task-driven and has grown to ~9 areas, so a flat tab bar was
 * getting long. Group them into sections with a vertical nav + a summary
 * landing. The `key` still maps to the `?tab=<key>` URL param (the folded-in
 * pages deep-link here), so the old redirects keep working.
 */
const SETTINGS_SECTIONS: {
  title: string;
  items: { key: string; label: string }[];
}[] = [
  {
    title: 'Organization',
    items: [
      { key: 'tenant', label: 'Tenant' },
      { key: 'contact', label: 'Contact' },
      { key: 'mailing', label: 'Address' },
      { key: 'license', label: 'License' },
    ],
  },
  {
    title: 'Security',
    items: [
      { key: 'security', label: 'Security' },
      { key: 'sso', label: 'Single sign-on' },
      { key: 'admins', label: 'Admins' },
      { key: 'audit', label: 'Audit log' },
    ],
  },
  {
    title: 'Capture & Metrics',
    items: [
      { key: 'capture-quality', label: 'Capture quality' },
      { key: 'recording', label: 'Recording' },
      { key: 'metrics', label: 'Metrics' },
      { key: 'compare', label: 'Compare' },
      { key: 'cohort', label: 'Cohort baseline' },
    ],
  },
  {
    title: 'Participants',
    items: [{ key: 'intake', label: 'Conference intake' }],
  },
  { title: 'System', items: [{ key: 'messages', label: 'Messages' }] },
];

/** Every valid `?tab=` key (overview + each section item). */
const VALID_KEYS = [
  'overview',
  ...SETTINGS_SECTIONS.flatMap((s) => s.items.map((i) => i.key)),
];

/** A clickable summary tile for the Settings overview landing. */
function SummaryCard({
  title,
  value,
  onClick,
}: {
  title: string;
  value: ReactNode;
  onClick: () => void;
}) {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        height: '100%',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** Section headings for the Metrics tab, keyed by metric modality. */
const MODALITY_SECTIONS: { modality: string; title: string }[] = [
  { modality: 'eeg', title: 'Cognitive (EEG)' },
  { modality: 'hand', title: 'Motion (hand IMU)' },
  { modality: 'both', title: 'General' },
];

/** Renders its children only when the active section key matches. */
function TabPanel({
  active,
  name,
  children,
}: {
  active: string;
  name: string;
  children: ReactNode;
}) {
  if (active !== name) return null;
  return <Box role="tabpanel">{children}</Box>;
}

/**
 * Tenant-admin Settings page.
 *
 * Shows the tenant_admin's own tenant as a read-only summary card
 * with an "Edit details" button that opens the existing
 * TenantFormDialog, restricted to the contact / address fields
 * (Slug, Billing mode, and is_active are superadmin-only and the
 * backend rejects those fields for non-superadmin callers — see
 * api/app/api/v1/routes/tenants.py:update_tenant).
 *
 * Implicit tenant id: the user's first active tenant_admin
 * membership picks which tenant they edit. Multi-tenant admins land
 * on the first until a topbar tenant switcher exists. Mirrors the
 * pattern in TenantParticipants.tsx.
 */
export function TenantSettings() {
  const { user, refresh: refreshUser } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);
  // Strict check, not `userHasRole` (which wildcards superadmin onto every
  // role): only a real superadmin may edit the SSO connection, and during an
  // act-as session the memberships are the assumed user's — so an
  // impersonated tenant_admin correctly gets the read-only view.
  const isSuperadmin = userHoldsExactRole(user, 'superadmin');

  const navigate = useNavigate();
  // Selection is URL-synced (`?tab=<key>`) so the folded-in pages (cohort /
  // admins / messages) can be deep-linked from their old routes. Unknown /
  // absent → the summary overview.
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const activeKey = rawTab && VALID_KEYS.includes(rawTab) ? rawTab : 'overview';
  const setActiveKey = (k: string) =>
    setParams({ tab: k }, { replace: true });
  // The folded-in tabs (admins / messages) portal their page-level actions
  // into this slot so they sit on the content header's line.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  // At-a-glance counts for the overview cards — fetched once, tolerant of
  // individual failures (a missing count just renders "—").
  const [counts, setCounts] = useState<{
    devices?: number;
    participants?: number;
    admins?: number;
    messages?: number;
  }>({});
  const [tenant, setTenant] = useState<tenantsApi.Tenant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [savingMfa, setSavingMfa] = useState(false);
  const [mfaNotice, setMfaNotice] = useState<string | null>(null);
  const [savingCompare, setSavingCompare] = useState(false);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [savingDirectEntry, setSavingDirectEntry] = useState(false);
  const [directEntryNotice, setDirectEntryNotice] = useState<string | null>(null);
  // Capture-quality floors (ROADMAP CAPTURE-RATE-THRESHOLDS). Local edit
  // state seeded from the tenant; saved together via one PATCH.
  const [eegPct, setEegPct] = useState(50);
  const [imuPct, setImuPct] = useState(50);
  const [savingRates, setSavingRates] = useState(false);
  const [ratesNotice, setRatesNotice] = useState<string | null>(null);
  // Scorecard metric visibility (ROADMAP SCORECARD-METRIC-VISIBILITY).
  // `catalogue` is the toggleable metric set; `hidden` is the local edit
  // state of hidden keys, seeded from the tenant and saved via one PATCH.
  const [catalogue, setCatalogue] = useState<MetricCatalogueItem[]>([]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [savingMetrics, setSavingMetrics] = useState(false);
  const [metricsNotice, setMetricsNotice] = useState<string | null>(null);
  // Per-procedure visibility. `selectedProcedureId` null = "All procedures"
  // (edit the tenant default); a procedure id = edit that procedure's
  // override. `savedHidden` is the last-persisted set for the current scope,
  // for dirty tracking. `procHasOverride` drives the Reset affordance.
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(
    null,
  );
  const [savedHidden, setSavedHidden] = useState<Set<string>>(new Set());
  const [procHasOverride, setProcHasOverride] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    try {
      const t = await tenantsApi.getTenant(tenantId);
      setTenant(t);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load tenant'));
      setTenant(null);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Seed the rate-floor inputs whenever the tenant (re)loads. The hidden-set
  // seeding is scope-aware and lives in its own effect below.
  useEffect(() => {
    if (tenant) {
      setEegPct(tenant.min_eeg_rate_pct);
      setImuPct(tenant.min_imu_rate_pct);
    }
  }, [tenant]);

  // The toggleable metric set is tenant-independent — load it once.
  useEffect(() => {
    getMetricCatalogue()
      .then(setCatalogue)
      .catch(() => setCatalogue([]));
  }, []);

  // The tenant's procedures, for the per-procedure scope picker.
  useEffect(() => {
    if (!tenantId) return;
    listProcedures(tenantId)
      .then(setProcedures)
      .catch(() => setProcedures([]));
  }, [tenantId]);

  // Seed the editable hidden-set for the current scope: the tenant default in
  // "All" mode, or the procedure's override (falling back to the tenant
  // default when it has none) for a selected procedure.
  useEffect(() => {
    if (!tenantId || !tenant) return;
    const applyDefault = () => {
      const seed = new Set(tenant.scorecard_hidden_metrics);
      setHidden(seed);
      setSavedHidden(new Set(seed));
    };
    if (selectedProcedureId === null) {
      setProcHasOverride(false);
      applyDefault();
      return;
    }
    let alive = true;
    getProcedureHiddenMetrics(tenantId, selectedProcedureId)
      .then((o) => {
        if (!alive) return;
        setProcHasOverride(o.has_override);
        const seed = new Set(
          o.has_override ? o.hidden_metrics : tenant.scorecard_hidden_metrics,
        );
        setHidden(seed);
        setSavedHidden(new Set(seed));
      })
      .catch(() => {
        if (alive) {
          setProcHasOverride(false);
          applyDefault();
        }
      });
    return () => {
      alive = false;
    };
  }, [tenantId, tenant, selectedProcedureId]);

  // Overview counts — devices / participants / admins / messages. Each is
  // optional; a rejected fetch just leaves its card at "—".
  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    void Promise.allSettled([
      devicesApi.listDevices(tenantId),
      tenantsApi.listParticipants(tenantId),
      tenantsApi.listTenantAdmins(tenantId),
      listTenantEvents(tenantId, { limit: 1 }),
    ]).then(([dev, par, adm, msg]) => {
      if (!alive) return;
      setCounts({
        devices: dev.status === 'fulfilled' ? dev.value.length : undefined,
        participants:
          par.status === 'fulfilled' ? par.value.length : undefined,
        admins: adm.status === 'fulfilled' ? adm.value.length : undefined,
        messages: msg.status === 'fulfilled' ? msg.value.total : undefined,
      });
    });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const activeMetrics = catalogue.length
    ? catalogue.length - hidden.size
    : 0;

  const onSaveRates = useCallback(async () => {
    if (!tenantId) return;
    setSavingRates(true);
    setError(null);
    try {
      const updated = await tenantsApi.updateTenant(tenantId, {
        min_eeg_rate_pct: eegPct,
        min_imu_rate_pct: imuPct,
      });
      setTenant(updated);
      setRatesNotice(
        'Minimum sampling rates saved. Capture devices pick them up on ' +
          'their next launch or sync.',
      );
    } catch (err: unknown) {
      setError(
        extractErrorMessage(err, 'failed to update minimum sampling rates'),
      );
    } finally {
      setSavingRates(false);
    }
  }, [tenantId, eegPct, imuPct]);

  const onSaveMetrics = useCallback(async () => {
    if (!tenantId) return;
    setSavingMetrics(true);
    setError(null);
    try {
      if (selectedProcedureId === null) {
        const updated = await tenantsApi.updateTenant(tenantId, {
          scorecard_hidden_metrics: [...hidden],
        });
        setTenant(updated);
        setMetricsNotice(
          'Tenant-default metric visibility saved — it applies to every ' +
            'procedure without its own override.',
        );
      } else {
        await setProcedureHiddenMetrics(tenantId, selectedProcedureId, [
          ...hidden,
        ]);
        setProcHasOverride(true);
        setMetricsNotice(
          'Per-procedure metric visibility saved — it overrides the tenant ' +
            'default for this procedure only.',
        );
      }
      setSavedHidden(new Set(hidden));
    } catch (err: unknown) {
      setError(
        extractErrorMessage(err, 'failed to update metric visibility'),
      );
    } finally {
      setSavingMetrics(false);
    }
  }, [tenantId, hidden, selectedProcedureId]);

  // Reset a procedure back to the tenant default (delete its override).
  const onResetProcedure = useCallback(async () => {
    if (!tenantId || !selectedProcedureId || !tenant) return;
    setSavingMetrics(true);
    setError(null);
    try {
      await clearProcedureHiddenMetrics(tenantId, selectedProcedureId);
      setProcHasOverride(false);
      const seed = new Set(tenant.scorecard_hidden_metrics);
      setHidden(seed);
      setSavedHidden(new Set(seed));
      setMetricsNotice('Reverted to the tenant default for this procedure.');
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to reset to tenant default'));
    } finally {
      setSavingMetrics(false);
    }
  }, [tenantId, selectedProcedureId, tenant]);

  // Metrics are dirty when the local hidden-set differs from what's persisted
  // for the current scope (tenant default or the selected procedure).
  const metricsDirty = useMemo(() => {
    if (savedHidden.size !== hidden.size) return true;
    for (const k of hidden) if (!savedHidden.has(k)) return true;
    return false;
  }, [savedHidden, hidden]);

  const onToggleMfa = useCallback(
    async (next: boolean) => {
      if (!tenantId || !tenant) return;
      setSavingMfa(true);
      setError(null);
      try {
        const updated = await tenantsApi.updateTenant(tenantId, {
          mfa_required: next,
        });
        setTenant(updated);
        setMfaNotice(
          next
            ? 'MFA is now required for every active tenant_admin of this tenant.'
            : 'MFA is no longer enforced. Tenant admins can manage TOTP individually from Profile.',
        );
      } catch (err: unknown) {
        setError(extractErrorMessage(err, 'failed to update MFA setting'));
      } finally {
        setSavingMfa(false);
      }
    },
    [tenantId, tenant],
  );

  const onToggleDirectEntry = useCallback(
    async (next: boolean) => {
      if (!tenantId || !tenant) return;
      setSavingDirectEntry(true);
      setError(null);
      try {
        const updated = await tenantsApi.updateTenant(tenantId, {
          direct_participant_entry: next,
        });
        setTenant(updated);
        setDirectEntryNotice(
          next
            ? 'Direct entry is on. Add participants from a form (email optional) — no email invitation is sent.'
            : 'Direct entry is off. New participants are added by sending a magic-link email invitation.',
        );
      } catch (err: unknown) {
        setError(
          extractErrorMessage(err, 'failed to update participant-enrollment mode'),
        );
      } finally {
        setSavingDirectEntry(false);
      }
    },
    [tenantId, tenant],
  );

  const onToggleCompare = useCallback(
    async (
      field: 'compare_enabled_tenant' | 'compare_enabled_participant' | 'ai_coach_use_debrief',
      next: boolean,
    ) => {
      if (!tenantId || !tenant) return;
      setSavingCompare(true);
      setError(null);
      try {
        const updated = await tenantsApi.updateTenant(tenantId, {
          [field]: next,
        });
        setTenant(updated);
      } catch (err: unknown) {
        setError(extractErrorMessage(err, 'failed to update Compare setting'));
      } finally {
        setSavingCompare(false);
      }
    },
    [tenantId, tenant],
  );

  const onSaveTimezone = useCallback(
    async (value: string) => {
      if (!tenantId || !tenant) return;
      setSavingTimezone(true);
      setError(null);
      try {
        // "" → viewer-local (server maps to NULL); "UTC" → force UTC.
        const updated = await tenantsApi.updateTenant(tenantId, {
          display_timezone: value,
        });
        setTenant(updated);
        // Refetch /users/me so the app-root DisplayTimeZoneProvider picks up the
        // new zone immediately — otherwise the change wouldn't show until a full
        // page reload (the provider reads the setting from the user payload).
        await refreshUser();
      } catch (err: unknown) {
        setError(extractErrorMessage(err, 'failed to update time-display setting'));
      } finally {
        setSavingTimezone(false);
      }
    },
    [tenantId, tenant, refreshUser],
  );

  if (!tenantId) {
    // Defence-in-depth — same warning shape TenantParticipants uses.
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    );
  }

  if (tenant === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Tidy reusable display helper — empty values render as an em-dash
  // so the card has stable shape regardless of how filled-in the
  // tenant's contact block is.
  const v = (s: string | null | undefined) => s ?? '—';

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h4">Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Configure your tenant — organization, security, capture &amp;
          metrics, and system.
        </Typography>
      </Box>

      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          these settings but can't edit tenant details or change the MFA
          policy.
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems="flex-start"
      >
        {/* Sectioned vertical nav. */}
        <Paper
          variant="outlined"
          sx={{
            width: { md: 230 },
            flexShrink: 0,
            alignSelf: 'stretch',
            overflow: 'hidden',
          }}
        >
          <List dense disablePadding>
            <ListItemButton
              selected={activeKey === 'overview'}
              onClick={() => setActiveKey('overview')}
            >
              <ListItemText
                primary="Overview"
                primaryTypographyProps={{ fontWeight: 700 }}
              />
            </ListItemButton>
            {SETTINGS_SECTIONS.map((section) => (
              <Box key={section.title}>
                <ListSubheader
                  disableSticky
                  sx={{
                    bgcolor: 'transparent',
                    color: 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    lineHeight: '2.4',
                  }}
                >
                  {section.title}
                </ListSubheader>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.key}
                    selected={activeKey === item.key}
                    onClick={() => setActiveKey(item.key)}
                    sx={{ pl: 3 }}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </Box>
            ))}
          </List>
        </Paper>

        {/* Content column. */}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          {/* Content header: per-tab action + the folded-in actions slot. */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
              minHeight: 40,
              mb: 1,
            }}
          >
            {activeKey === 'contact' && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setEditing(true)}
                disabled={!canWrite}
              >
                Edit details
              </Button>
            )}
            <Box
              ref={setActionsSlot}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            />
          </Box>

          {/* Overview landing — summary cards, click to jump. */}
          {activeKey === 'overview' && (
            <Grid container spacing={2}>
              {[
                {
                  title: 'Tenant',
                  value: tenant.name,
                  go: () => setActiveKey('tenant'),
                },
                {
                  title: 'Security',
                  value: tenant.mfa_required ? 'MFA on' : 'MFA off',
                  go: () => setActiveKey('security'),
                },
                {
                  title: 'Metrics',
                  value: `${activeMetrics} active`,
                  go: () => setActiveKey('metrics'),
                },
                {
                  title: 'Admins',
                  value: counts.admins ?? '—',
                  go: () => setActiveKey('admins'),
                },
                {
                  title: 'Messages',
                  value: counts.messages ?? '—',
                  go: () => setActiveKey('messages'),
                },
                {
                  title: 'Devices',
                  value: counts.devices ?? '—',
                  go: () => navigate('/tenant/devices'),
                },
                {
                  title: 'Participants',
                  value: counts.participants ?? '—',
                  go: () => navigate('/tenant/participants'),
                },
              ].map((c) => (
                <Grid key={c.title} size={{ xs: 6, sm: 4, md: 3 }}>
                  <SummaryCard title={c.title} value={c.value} onClick={c.go} />
                </Grid>
              ))}
            </Grid>
          )}

      <TabPanel active={activeKey} name="tenant">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Tenant
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Name
              </Typography>
              <Typography>{tenant.name}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Slug
              </Typography>
              <Typography sx={{ fontFamily: 'monospace' }}>
                {tenant.slug}
              </Typography>
              <Chip label="immutable" size="small" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Billing mode
              </Typography>
              <Typography>{tenant.billing_mode.replace('_', ' ')}</Typography>
              <Chip label="managed by 10NEWTONS" size="small" variant="outlined" />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Status
              </Typography>
              {tenant.is_active ? (
                <Chip label="Active" size="small" color="success" />
              ) : (
                <Chip label="Suspended" size="small" color="warning" />
              )}
            </Stack>
            {/* Read-only reference fields admins occasionally need. */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Tenant ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {tenant.id}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 140 }}>
                Created
              </Typography>
              <Typography>
                {formatDate(tenant.created_at)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Participant enrollment
          </Typography>
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={tenant.direct_participant_entry}
                disabled={savingDirectEntry || !canWrite}
                onChange={(e) => void onToggleDirectEntry(e.target.checked)}
              />
            }
            label="Direct participant entry (skip email invitations)"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When ON, you add participants from a form (name + optional email)
            straight from the Participants page — no email invitation is sent.
            Built for live demos and sessions where an email round-trip makes no
            sense. When OFF, participants are invited by a magic-link email they
            accept to set their password.
          </Typography>
          {directEntryNotice && (
            <Alert
              severity="success"
              onClose={() => setDirectEntryNotice(null)}
              sx={{ mt: 2 }}
            >
              {directEntryNotice}
            </Alert>
          )}
        </CardContent>
      </Card>
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Time display
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            How dates and times appear across the portal for this tenant.
            Recordings are always stored in UTC — this only changes how they&apos;re
            displayed. Choose UTC for consistency across sites in different time
            zones, or keep each viewer&apos;s local device zone.
          </Typography>
          <SearchableSelect
            label="Show times in"
            value={tenant.display_timezone ?? ''}
            disabled={savingTimezone || !canWrite}
            onChange={(v) => void onSaveTimezone(v)}
            pinned={TZ_DEFAULT_OPTION}
            options={TZ_OPTIONS}
            sx={{ minWidth: 320 }}
          />
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel active={activeKey} name="security">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Security
          </Typography>
          <Divider sx={{ my: 1 }} />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Switch
                checked={tenant.mfa_required}
                disabled={savingMfa || !canWrite}
                onChange={(e) => void onToggleMfa(e.target.checked)}
              />
            }
            label="Require MFA for tenant admins"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            When ON, every active tenant_admin of this tenant must enroll TOTP
            on their next sign-in and cannot disable it from their Profile.
            When OFF, MFA falls back to each admin's individual Profile
            setting.
          </Typography>
          {mfaNotice && (
            <Alert
              severity="success"
              onClose={() => setMfaNotice(null)}
              sx={{ mt: 2 }}
            >
              {mfaNotice}
            </Alert>
          )}
        </CardContent>
      </Card>
      </TabPanel>

      {/* Enterprise SSO (ENTERPRISE-SSO) — the tenant's own view of its
          identity-provider connection. Reads are open to tenant admins;
          writes are superadmin-only server-side (a domain maps to exactly
          one tenant platform-wide), so anyone but a real superadmin gets the
          read-only rendering. */}
      <TabPanel active={activeKey} name="sso">
        <SsoConnectionPanel tenantId={tenant.id} readOnly={!isSuperadmin} />
      </TabPanel>

      <TabPanel active={activeKey} name="capture-quality">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Capture quality
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Minimum acceptable sensor sampling rate, as a percentage of each
            sensor's target (EEG 256 Hz, hand IMU 50 Hz). When a sensor holds
            below the floor, the capture-app makes the operator acknowledge
            before and during recording — it never blocks a capture outright.
            Set 0 to turn the prompt off. Devices apply the change on their
            next launch or sync.
          </Typography>
          {/* Sliders read more naturally than a bare number for a 0–100%
              floor; the target-Hz translation rides alongside. */}
          <Stack spacing={3} sx={{ maxWidth: 560, mt: 1 }}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  EEG minimum
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {eegPct}%{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ≈ {Math.round((eegPct / 100) * 256)} Hz of 256 Hz
                  </Typography>
                </Typography>
              </Stack>
              <Slider
                value={eegPct}
                min={0}
                max={100}
                disabled={!canWrite || savingRates}
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}%`}
                onChange={(_e, x) => setEegPct(x as number)}
                aria-label="EEG minimum sampling rate percent"
              />
            </Box>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Hand IMU minimum
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {imuPct}%{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ≈ {Math.round((imuPct / 100) * 50)} Hz of 50 Hz
                  </Typography>
                </Typography>
              </Stack>
              <Slider
                value={imuPct}
                min={0}
                max={100}
                disabled={!canWrite || savingRates}
                valueLabelDisplay="auto"
                valueLabelFormat={(x) => `${x}%`}
                onChange={(_e, x) => setImuPct(x as number)}
                aria-label="Hand IMU minimum sampling rate percent"
              />
            </Box>
          </Stack>
          {canWrite && (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={
                savingRates ||
                !tenant ||
                (eegPct === tenant.min_eeg_rate_pct &&
                  imuPct === tenant.min_imu_rate_pct)
              }
              onClick={() => void onSaveRates()}
            >
              {savingRates ? 'Saving…' : 'Save minimum rates'}
            </Button>
          )}
          {ratesNotice && (
            <Alert
              severity="success"
              onClose={() => setRatesNotice(null)}
              sx={{ mt: 2 }}
            >
              {ratesNotice}
            </Alert>
          )}
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel active={activeKey} name="metrics">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Scorecard metrics
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose which metrics appear on the Session-metric scorecard and
            the cognitive trace. Unchecked metrics are hidden from the
            tenant-admin drill-down, each participant's own dashboard, and the
            capture app. Stress and Fatigue exist only in the cognitive trace.
            Set it as the tenant default, or override it per procedure. New
            metrics are shown by default.
          </Typography>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <SearchableSelect
              label="Applies to"
              value={selectedProcedureId ?? ''}
              disabled={savingMetrics}
              onChange={(v) => setSelectedProcedureId(v ? v : null)}
              pinned={ALL_PROCEDURES_OPTION}
              options={procedures.map((p) => ({ value: p.id, label: p.name }))}
              sx={{ minWidth: 280 }}
            />
            {selectedProcedureId !== null && (
              <Chip
                size="small"
                variant="outlined"
                color={procHasOverride ? 'primary' : 'default'}
                label={
                  procHasOverride
                    ? 'Overriding tenant default'
                    : 'Using tenant default'
                }
              />
            )}
          </Box>
          {catalogue.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No metrics available.
            </Typography>
          ) : (
            MODALITY_SECTIONS.map((section) => {
              const items = catalogue.filter(
                (m) => m.modality === section.modality,
              );
              if (items.length === 0) return null;
              return (
                <Box key={section.modality} sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    {section.title}
                  </Typography>
                  <FormGroup>
                    {items.map((m) => (
                      <FormControlLabel
                        key={m.key}
                        control={
                          <Checkbox
                            size="small"
                            checked={!hidden.has(m.key)}
                            disabled={!canWrite || savingMetrics}
                            onChange={(e) => {
                              const show = e.target.checked;
                              setHidden((prev) => {
                                const next = new Set(prev);
                                if (show) next.delete(m.key);
                                else next.add(m.key);
                                return next;
                              });
                            }}
                          />
                        }
                        label={
                          m.trace_only ? (
                            <>
                              {prettyMetric(m.key)}{' '}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                              >
                                (cognitive trace only)
                              </Typography>
                            </>
                          ) : (
                            prettyMetric(m.key)
                          )
                        }
                      />
                    ))}
                  </FormGroup>
                </Box>
              );
            })
          )}
          {canWrite && catalogue.length > 0 && (
            <Button
              variant="contained"
              sx={{ mt: 1 }}
              disabled={savingMetrics || !metricsDirty}
              onClick={() => void onSaveMetrics()}
            >
              {savingMetrics ? 'Saving…' : 'Save metric visibility'}
            </Button>
          )}
          {canWrite &&
            selectedProcedureId !== null &&
            procHasOverride && (
              <Button
                variant="text"
                sx={{ mt: 1, ml: 1 }}
                disabled={savingMetrics}
                onClick={() => void onResetProcedure()}
              >
                Reset to tenant default
              </Button>
            )}
          {metricsNotice && (
            <Alert
              severity="success"
              onClose={() => setMetricsNotice(null)}
              sx={{ mt: 2 }}
            >
              {metricsNotice}
            </Alert>
          )}
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel active={activeKey} name="contact">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Contact
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Contact name
              </Typography>
              <Typography>{v(tenant.contact_name)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Contact email
              </Typography>
              <Typography>{v(tenant.contact_email)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Phone
              </Typography>
              <Typography>{v(tenant.phone)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Fax
              </Typography>
              <Typography>{v(tenant.fax)}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">
                Website
              </Typography>
              <Typography>{v(tenant.website)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel active={activeKey} name="mailing">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Mailing address
          </Typography>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">
                Address line 1
              </Typography>
              <Typography>{v(tenant.address_line1)}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="caption" color="text.secondary">
                Address line 2
              </Typography>
              <Typography>{v(tenant.address_line2)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                City
              </Typography>
              <Typography>{v(tenant.city)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                State / region
              </Typography>
              <Typography>{v(tenant.state_region)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Postal code
              </Typography>
              <Typography>{v(tenant.postal_code)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="caption" color="text.secondary">
                Country
              </Typography>
              <Typography>{v(tenant.country)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      </TabPanel>

      <TabPanel active={activeKey} name="compare">
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Compare view
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Compare puts two sides head-to-head on one procedure — a
              participant, an experience band, or a group. Choose where it&apos;s
              available.
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={tenant.compare_enabled_tenant}
                  disabled={savingCompare || !canWrite}
                  onChange={(e) =>
                    void onToggleCompare(
                      'compare_enabled_tenant',
                      e.target.checked,
                    )
                  }
                />
              }
              label="Available on the tenant dashboard"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, mb: 1.5 }}
            >
              Shows the Benchmark tab in Analysis so staff can compare any
              participants or groups.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={tenant.compare_enabled_participant}
                  disabled={savingCompare || !canWrite}
                  onChange={(e) =>
                    void onToggleCompare(
                      'compare_enabled_participant',
                      e.target.checked,
                    )
                  }
                />
              }
              label="Available on the participant dashboard"
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Lets each participant compare themselves against an experience
              band or the whole cohort — anonymised, never other named
              participants.
            </Typography>
          </CardContent>
        </Card>

        {/* AI-COACH-FROM-COACHING-TRANSCRIPT: opt in to letting the AI Coach
            read coaching debriefs. Off by default — the coach is otherwise
            metrics-only. */}
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              AI Coach
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(tenant.ai_coach_use_debrief)}
                  disabled={savingCompare || !canWrite}
                  onChange={(e) =>
                    void onToggleCompare('ai_coach_use_debrief', e.target.checked)
                  }
                />
              }
              label="Let the AI Coach use coaching debriefs"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              When a session has a coaching debrief (uploaded as Coaching video
              or audio), the AI Coach pulls out the coach&apos;s main points —
              quoting their exact words — and checks each against the
              session&apos;s metrics. The debrief transcript, with patient
              identifiers removed, is sent to the AI provider; no audio or video
              and no participant names. Off: the AI Coach uses metrics only.
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Folded-in former pages, as tabs. */}
      <TabPanel active={activeKey} name="cohort">
        <CohortBaseline embedded />
      </TabPanel>
      <TabPanel active={activeKey} name="admins">
        <TenantAdmins embedded actionsSlot={actionsSlot} />
      </TabPanel>
      <TabPanel active={activeKey} name="audit">
        {/* Tenant-scoped audit trail — who changed access, when, from where.
            Reuses the admin AuditTrail against the tenant-scoped endpoint. */}
        <AuditTrail embedded tenantId={tenantId} actionsSlot={actionsSlot} />
      </TabPanel>
      <TabPanel active={activeKey} name="license">
        <TenantLicensePanel tenantId={tenant.id} />
      </TabPanel>
      <TabPanel active={activeKey} name="recording">
        <RecordingSettings tenant={tenant} canWrite={canWrite} onTenantChange={setTenant} />
      </TabPanel>
      <TabPanel active={activeKey} name="intake">
        {/* Conference intake — booth enrollment with consent + survey. */}
        <IntakeSettings tenantId={tenant.id} canWrite={canWrite} />
      </TabPanel>
      <TabPanel active={activeKey} name="messages">
        <TenantMessages embedded actionsSlot={actionsSlot} />
      </TabPanel>
        </Box>
      </Stack>

      {editing && (
        <TenantFormDialog
          mode="edit"
          tenant={tenant}
          restrictToContactFields
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void reload();
          }}
        />
      )}
    </Stack>
  );
}
